import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Clock, CheckCircle2, X, Languages } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { SERVICES } from "../data/services";

const getText = (service, lang) => {
  if (lang === "hi" && service.hi) {
    return {
      name: service.hi.name,
      duration: service.hi.duration,
      summary: service.hi.summary,
      what: service.hi.what,
      symptoms: service.hi.symptoms,
      how: service.hi.how,
      benefits: service.hi.benefits,
    };
  }
  return {
    name: service.name,
    duration: service.duration,
    summary: service.summary,
    what: service.what,
    symptoms: service.symptoms,
    how: service.how,
    benefits: service.benefits,
  };
};

const UI_TEXT = {
  en: {
    sectionLabel: "Our Services",
    heading1: "Treatments designed around ",
    heading2: "your recovery",
    heading3: ".",
    description:
      "From back pain to neurological rehabilitation — every treatment is customised for your condition and delivered at the comfort of your home.",
    startingAt: "Starting at",
    perDay: "/day",
    learnMore: "Learn More",
    whatIs: "What is",
    symptoms: "Common Symptoms",
    howHelps: "How Physiotherapy Helps",
    benefits: "Benefits",
    recommendedDuration: "Recommended Duration",
    finalPlan: "Final plan is adjusted by the doctor based on your assessment.",
    bookTreatment: "Book This Treatment",
    close: "Close",
    toggleLabel: "हिन्दी में पढ़ें",
  },
  hi: {
    sectionLabel: "हमारी सेवाएँ",
    heading1: "आपकी रिकवरी के लिए ",
    heading2: "बनाए गए इलाज",
    heading3: "।",
    description:
      "कमर दर्द से लेकर न्यूरोलॉजिकल रिहैबिलिटेशन तक — हर इलाज आपकी स्थिति के अनुसार अनुकूलित है और आपके घर के आराम से प्रदान किया जाता है।",
    startingAt: "शुरुआती कीमत",
    perDay: "/दिन",
    learnMore: "और जानें",
    whatIs: "क्या है",
    symptoms: "सामान्य लक्षण",
    howHelps: "फिजियोथेरेपी कैसे मदद करती है",
    benefits: "फ़ायदे",
    recommendedDuration: "अनुशंसित अवधि",
    finalPlan: "आपके आकलन के आधार पर डॉक्टर द्वारा अंतिम योजना तय की जाती है।",
    bookTreatment: "इस इलाज की बुकिंग करें",
    close: "बंद करें",
    toggleLabel: "Read in English",
  },
};

const ServiceCard = ({ service, onLearnMore, index, lang }) => {
  const t = getText(service, lang);
  const ui = UI_TEXT[lang];
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.5, delay: (index % 3) * 0.08 }}
      data-testid={`service-card-${service.id}`}
      className="group bg-white rounded-3xl border border-slate-100 overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_50px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300 flex flex-col"
    >
      <div className="aspect-[16/10] overflow-hidden bg-slate-50">
        <img
          src={service.image}
          alt={t.name}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
        />
      </div>
      <div className="p-6 flex flex-col flex-1">
        <h3 className="font-display text-xl font-semibold text-slate-900 tracking-tight">
          {t.name}
        </h3>
        <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-2">
          {t.summary}
        </p>
        <div className="mt-5 flex items-end justify-between gap-3">
          <div>
            <p className="text-xs text-slate-500">{ui.startingAt}</p>
            <p className="font-display text-2xl font-semibold text-slate-900">
              ₹{service.price}
              <span className="text-sm font-medium text-slate-500">{ui.perDay}</span>
            </p>
          </div>
          <Button
            variant="ghost"
            onClick={() => onLearnMore(service)}
            data-testid={`service-learnmore-${service.id}`}
            className="rounded-full text-brand-700 hover:bg-brand-50 hover:text-brand-800 px-4 h-10 font-medium"
          >
            {ui.learnMore}
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </motion.article>
  );
};

export const Services = ({ onBookTreatment }) => {
  const [active, setActive] = useState(null);
  const [lang, setLang] = useState("en");
  const open = Boolean(active);
  const ui = UI_TEXT[lang];

  const handleBook = (service) => {
    setActive(null);
    if (onBookTreatment) onBookTreatment(service.id);
    setTimeout(() => {
      document.querySelector("#booking")?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-20 sm:py-28 lg:py-32 bg-slate-50/60"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="max-w-3xl">
            <p className="text-xs sm:text-sm text-brand-600 font-semibold tracking-[0.18em] uppercase">
              {ui.sectionLabel}
            </p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900">
              {ui.heading1}<em className="not-italic bg-gradient-to-r from-brand-400 to-accent-400 bg-clip-text text-transparent">{ui.heading2}</em>{ui.heading3}
            </h2>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">
              {ui.description}
            </p>
          </div>
          <button
            onClick={() => setLang(lang === "en" ? "hi" : "en")}
            className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-brand-300 transition-colors"
          >
            <Languages className="w-4 h-4 text-brand-600" />
            {ui.toggleLabel}
          </button>
        </div>

        <div className="mt-12 sm:mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {SERVICES.map((s, i) => (
            <ServiceCard
              key={s.id}
              service={s}
              index={i}
              lang={lang}
              onLearnMore={setActive}
            />
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent
          data-testid="service-modal"
          className="max-w-3xl rounded-3xl p-0 overflow-hidden border-slate-100 max-h-[92vh] flex flex-col"
        >
          {active && (() => {
            const t = getText(active, lang);
            return (
              <>
                <div className="relative h-56 sm:h-64 bg-slate-100 shrink-0">
                  <img
                    src={active.image}
                    alt={t.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                  <button
                    onClick={() => setActive(null)}
                    data-testid="service-modal-close"
                    className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/90 backdrop-blur border border-white flex items-center justify-center hover:bg-white"
                    aria-label="Close"
                  >
                    <X className="w-4 h-4 text-slate-700" />
                  </button>
                  <div className="absolute bottom-4 left-5 right-5 text-white">
                    <DialogHeader className="text-left">
                      <DialogTitle className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
                        {t.name}
                      </DialogTitle>
                      <DialogDescription className="text-white/80 text-sm sm:text-base">
                        {lang === "en" ? "Starting at" : "शुरुआती कीमत"} ₹{active.price}{ui.perDay} · {t.duration}
                      </DialogDescription>
                    </DialogHeader>
                  </div>
                </div>

                <div className="p-6 sm:p-8 space-y-6 overflow-y-auto">
                  <div>
                    <h4 className="font-display text-lg font-semibold text-slate-900">
                      {ui.whatIs} {t.name}?
                    </h4>
                    <p className="mt-2 text-slate-600 leading-relaxed">{t.what}</p>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-display text-lg font-semibold text-slate-900">
                        {ui.symptoms}
                      </h4>
                      <ul className="mt-3 space-y-2">
                        {t.symptoms.map((s, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-slate-700 text-sm">
                            <span className="w-1.5 h-1.5 rounded-full bg-brand-500 mt-2 shrink-0" />
                            {s}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-display text-lg font-semibold text-slate-900">
                        {ui.howHelps}
                      </h4>
                      <ul className="mt-3 space-y-2">
                        {t.how.map((s, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-slate-700 text-sm">
                            <CheckCircle2 className="w-4 h-4 text-brand-600 mt-0.5 shrink-0" strokeWidth={1.8} />
                            {s}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-display text-lg font-semibold text-slate-900">
                        {ui.benefits}
                      </h4>
                      <ul className="mt-3 space-y-2">
                        {t.benefits.map((s, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-slate-700 text-sm">
                            <CheckCircle2 className="w-4 h-4 text-accent-600 mt-0.5 shrink-0" strokeWidth={1.8} />
                            {s}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="rounded-2xl bg-brand-50/60 border border-brand-100 p-5">
                      <div className="flex items-center gap-2 text-brand-700">
                        <Clock className="w-4 h-4" strokeWidth={1.8} />
                        <p className="text-xs font-semibold tracking-[0.15em] uppercase">
                          {ui.recommendedDuration}
                        </p>
                      </div>
                      <p className="font-display text-2xl font-semibold text-slate-900 mt-2">
                        {t.duration}
                      </p>
                      <p className="text-sm text-slate-600 mt-1">
                        {ui.finalPlan}
                      </p>
                    </div>
                  </div>

                  <div className="pt-2 flex flex-col sm:flex-row gap-3">
                    <Button
                      onClick={() => handleBook(active)}
                      data-testid="service-modal-book-btn"
                      className="rounded-full bg-brand-500 hover:bg-brand-600 text-white h-12 px-6 text-base shadow-md shadow-brand-500/25 flex-1"
                    >
                      {ui.bookTreatment}
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setActive(null)}
                      className="rounded-full h-12 px-6 text-base border-slate-200 text-slate-700"
                    >
                      {ui.close}
                    </Button>
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </section>
  );
};
